import React from "react";
import {
  Bot,
  Camera,
  CalendarDays,
  ChefHat,
  MessageSquareText,
  Newspaper,
  Scale,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import BottomSheet from "./ui/BottomSheet";
import telegramService from "../utils/telegram";
import apiService from "../services/api";
import { useNavigation } from "../navigation/NavigationContext";

interface QuickActionsSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const QuickActionsSheet: React.FC<QuickActionsSheetProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { setTab, push } = useNavigation();

  const handleLogFood = () => {
    telegramService.haptic("medium");
    const tgId = telegramService.getUserId();
    if (tgId) {
      apiService.promptLog(tgId).catch(() => {});
    }
    telegramService.close();
  };

  const handleLogWeight = () => {
    onClose();
    setTab("stats");
  };

  const handleOpenPlan = () => {
    onClose();
    setTab("plan");
  };

  const handleOpenCommunity = () => {
    onClose();
    push("lenta");
  };

  const supportingActions = [
    {
      icon: Scale,
      tone: "bg-info-soft text-info",
      title: t("quickActions.logWeight"),
      description: t("quickActions.logWeightDesc"),
      onClick: handleLogWeight,
    },
    {
      icon: CalendarDays,
      tone: "bg-premium-soft text-premium",
      title: t("quickActions.openPlan"),
      description: t("quickActions.openPlanDesc"),
      onClick: handleOpenPlan,
    },
    {
      icon: ChefHat,
      tone: "bg-success-soft text-success",
      title: t("quickActions.findMeal"),
      description: t("quickActions.findMealDesc"),
      onClick: handleOpenPlan,
    },
  ];

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={t("quickActions.title")}>
      <div className="space-y-4">
        <button
          onClick={handleLogFood}
          className="w-full overflow-hidden rounded-[1.75rem] bg-content-primary p-5 text-left text-content-inverse shadow-elevated active:scale-[0.985] transition-transform"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-white/12 text-white">
                <Camera size={23} />
              </div>
              <h3 className="text-lg font-black">{t("quickActions.logFood")}</h3>
              <p className="mt-1 text-sm font-medium text-content-inverse/70">
                {t("quickActions.logFoodDesc")}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1 rounded-full bg-white/10 px-3 py-1.5 text-[11px] font-black">
              <Bot size={13} />
              {t("quickActions.bot")}
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-2">
            <div className="rounded-2xl bg-white/10 px-3 py-2">
              <div className="flex items-center gap-2 text-xs font-bold">
                <Camera size={14} />
                {t("quickActions.photo")}
              </div>
            </div>
            <div className="rounded-2xl bg-white/10 px-3 py-2">
              <div className="flex items-center gap-2 text-xs font-bold">
                <MessageSquareText size={14} />
                {t("quickActions.textVoice")}
              </div>
            </div>
          </div>
        </button>

        <div className="grid grid-cols-3 gap-2">
          {supportingActions.map((action) => (
            <button
              key={action.title}
              onClick={action.onClick}
              className="min-h-[128px] rounded-3xl border border-line bg-surface p-3 text-left shadow-card active:scale-[0.98] transition-transform"
            >
              <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-2xl ${action.tone}`}>
                <action.icon size={19} />
              </div>
              <h3 className="text-xs font-black leading-tight text-content-primary">{action.title}</h3>
              <p className="mt-1 text-[10px] font-medium leading-snug text-content-tertiary">
                {action.description}
              </p>
            </button>
          ))}
        </div>

        <div className="rounded-3xl border border-line bg-inset p-3">
          <button
            onClick={handleOpenCommunity}
            className="flex w-full items-center gap-3 rounded-2xl px-2 py-2 text-left active:scale-[0.98] transition-transform"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-surface text-content-secondary">
              <Newspaper size={18} />
            </div>
            <div className="min-w-0">
              <h3 className="text-xs font-black text-content-primary">{t("quickActions.createPost")}</h3>
              <p className="text-[11px] font-medium text-content-tertiary">
                {t("quickActions.createPostDesc")}
              </p>
            </div>
          </button>
        </div>
      </div>
    </BottomSheet>
  );
};

export default QuickActionsSheet;
